import { useStacklineTheme } from '@stackline/ui';
import React from 'react';
import {
  BeaconProSidebarFilters,
  ICON_STYLES
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/Filters/constants';
import CheckboxItem from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/CheckboxItem';
import GenericSidebarFilter, {
  GenericSidebarFilterProps
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/components/GenericSidebarFilter';
import {
  FILTER_OPERATION,
  useSavedSearchBuilder
} from 'src/components/BeaconV2Components/ExperimentalLayout/Topics/TopicBuilder/hooks/useSavedSearchBuilder';
import { SlSidebarScrollableContainer } from 'src/components/BeaconV2Components/SlSidebar';
import { SlExcludeSelectedCheckbox, SlSelectedCheckbox } from 'src/components/SvgIcons';
import { useAppSelector } from 'src/utils/Hooks';

const LegacySubcategorySidebarFilter = (props: GenericSidebarFilterProps) => {
  const theme = useStacklineTheme();

  const { formData, updateSavedSearch } = useSavedSearchBuilder();

  const selectedParentCategories = (
    formData.termFilters?.categoryId.values.length
      ? formData.termFilters?.categoryId.values
      : formData.termFilters?.excludedCategoryId.values.length
      ? formData.termFilters?.excludedCategoryId.values
      : []
  ).map((value) => value.i);

  const selectedSubcategories = (
    formData.termFilters?.subCategoryId.values.length
      ? formData.termFilters?.subCategoryId.values
      : formData.termFilters?.excludedSubCategoryId.values.length
      ? formData.termFilters?.excludedSubCategoryId.values
      : []
  ).map((value) => ({ name: value.n, id: value.i }));

  const subcategoryOptions = useAppSelector((state) =>
    state.subCategories.map((subcategory) => ({
      ...subcategory,
      selected: selectedSubcategories.some(
        (selectedSubcategory) => Number(selectedSubcategory.id) === Number(subcategory.id)
      )
    }))
  );

  /**
   * Search input for subcategories
   */
  const [currentInput, setCurrentInput] = React.useState<string>('');

  const isExcluded = formData.termFilters?.excludedSubCategoryId.values.length > 0;
  /**
   * Toggle mode for include/exclude; determines which icon to render in the renderItems function.
   */
  const [toggleMode, setToggleMode] = React.useState<'include' | 'exclude'>(isExcluded ? 'exclude' : 'include');

  /**
   * Handles the selection of an subcategory
   */
  const handleItemSelect = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (toggleMode === 'include') {
        updateSavedSearch({
          key: BeaconProSidebarFilters.SubCategory.filterKey,
          newValue: value,
          operation: FILTER_OPERATION.Toggle
        });
      } else {
        updateSavedSearch({
          key: BeaconProSidebarFilters.ExcludedSubCategory.filterKey,
          newValue: value,
          operation: FILTER_OPERATION.Toggle
        });
      }
    },
    [toggleMode, updateSavedSearch]
  );

  const getIcon = React.useCallback(() => {
    switch (toggleMode) {
      case 'include': {
        return <SlSelectedCheckbox style={ICON_STYLES} />;
      }

      case 'exclude': {
        return <SlExcludeSelectedCheckbox style={ICON_STYLES} />;
      }
      default:
        return null;
    }
  }, [toggleMode]);

  /**
   * Renders the list of items as checkboxes
   */
  const renderItems = React.useCallback(() => {
    return (
      <SlSidebarScrollableContainer>
        {subcategoryOptions
          ?.filter(
            (subcategory) =>
              subcategory.name.toLowerCase().includes(currentInput.toLowerCase()) &&
              (selectedParentCategories.length === 0 ||
                selectedParentCategories.includes(String(subcategory.parentCategoryId)) ||
                subcategory.selected)
            // And the parent category is selected if any are selected
          )
          .sort((a, b) => (a.selected === b.selected ? 0 : a.selected ? -1 : 1)) // sort by checked items
          .map((subcategory, index) => (
            <CheckboxItem
              onClick={handleItemSelect}
              value={subcategory.id}
              key={index}
              label={subcategory.name}
              theme={theme}
              checked={subcategory.selected}
              checkedIcon={getIcon()}
            />
          ))}
      </SlSidebarScrollableContainer>
    );
  }, [currentInput, theme, handleItemSelect, selectedParentCategories, subcategoryOptions, getIcon]);

  /**
   * Handles the change of the input
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentInput(e.target.value);
  };

  const handleClear = () => {
    updateSavedSearch({
      key: BeaconProSidebarFilters.SubCategory.filterKey,
      callback: (form) => {
        form.termFilters[BeaconProSidebarFilters.SubCategory.filterKey].values = [];
        form.termFilters[BeaconProSidebarFilters.ExcludedSubCategory.filterKey].values = [];
        return form;
      },
      operation: null,
      newValue: null
    });

    setCurrentInput('');
  };

  const handleToggle = (toggle: 'include' | 'exclude') => {
    setToggleMode(toggle);
    if (toggle === 'include') {
      updateSavedSearch({
        key: BeaconProSidebarFilters.SubCategory.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.SubCategory.filterKey].values = selectedSubcategories.map(
            (subcategory) => ({
              i: subcategory.id,
              n: subcategory.name
            })
          );

          form.termFilters[BeaconProSidebarFilters.ExcludedSubCategory.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    } else {
      updateSavedSearch({
        key: BeaconProSidebarFilters.ExcludedSubCategory.filterKey,
        callback: (form) => {
          form.termFilters[BeaconProSidebarFilters.ExcludedSubCategory.filterKey].values = selectedSubcategories.map(
            (subcategory) => ({
              i: subcategory.id,
              n: subcategory.name
            })
          );

          form.termFilters[BeaconProSidebarFilters.SubCategory.filterKey].values = [];

          return form;
        },
        operation: null,
        newValue: null
      });
    }
  };

  return (
    <GenericSidebarFilter
      {...props}
      renderItems={renderItems}
      handleClear={handleClear}
      handleToggle={handleToggle}
      toggleMode={toggleMode}
      includeClear={selectedSubcategories?.length > 0}
      sidebarInputProps={{
        ...props.sidebarInputProps,
        value: currentInput,
        onChange: handleChange
      }}
    />
  );
};

export default LegacySubcategorySidebarFilter;
